import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Calendar, Clock, FileText, MapPin, CalendarClock, Plane,
  ArrowLeftRight, DollarSign, Route, RefreshCw, ChevronRight, Timer,
} from 'lucide-react';

/**
 * My Shifts hub — approved grouped layout. Organises and deep-links to the
 * EXISTING modules; no duplicate forms or endpoints. Summary cards use:
 *  • Next Shift + Hours This Week → GET /api/staff/my-shifts-hub
 *    (hours = SCHEDULED rostered hours Mon–Sun, labelled as such)
 *  • Open Shifts → existing GET /roster/open-shifts (server-side eligibility)
 *  • Pending Requests → existing per-type endpoints, one count per type
 */
const GROUPS = [
  {
    title: 'Roster & Open Shifts',
    items: [
      { key: 'roster', title: 'Roster', icon: Calendar, path: '/staff/roster', permissions: ['roster.view'] },
      { key: 'openshifts', title: 'Open Shifts', icon: MapPin, path: '/staff/open-shifts', permissions: ['roster.open_shift_pickup'] },
      { key: 'swaps', title: 'Shift Swaps', icon: ArrowLeftRight, path: '/my/swap-requests', permissions: ['roster.request_swap'] },
    ],
  },
  {
    title: 'Time & Attendance',
    items: [
      { key: 'clock', title: 'Clock In / Out', icon: Clock, path: '/my/clock', permissions: ['attendance.clock_in', 'attendance.clock_out'] },
      { key: 'timesheets', title: 'Timesheets', icon: FileText, path: '/timesheets/my-v2', permissions: ['timesheets.view_own'] },
    ],
  },
  {
    title: 'Leave & Availability',
    items: [
      { key: 'leave', title: 'Leave', icon: Plane, path: '/portal/leave', permissions: ['leave.request'] },
      { key: 'availability', title: 'Availability', icon: CalendarClock, path: '/portal/availability', permissions: ['availability.manage'] },
    ],
  },
  {
    title: 'Travel & Expenses',
    items: [
      { key: 'trips', title: 'Trip Logs', icon: Route, path: '/my/trip-logs', permissions: ['transport.log', 'transport.view_own'] },
      { key: 'mileage', title: 'Mileage', icon: Route, path: '/my/trip-logs', permissions: ['transport.log'], note: 'Kilometres are captured on Trip Logs' },
      { key: 'expenses', title: 'Expenses', icon: DollarSign, path: '/my/expenses', permissions: ['expenses.submit', 'expenses.view_own'] },
    ],
  },
];

export default function MyWorkPage() {
  const { hasPermission } = useAuth();
  const [hub, setHub] = useState(null);
  const [openShiftCount, setOpenShiftCount] = useState(null);
  const [pending, setPending] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    const cnt = (r) => {
      if (r?.status !== 'fulfilled' || !r.value) return null;
      const d = r.value.data || {};
      return d.count ?? d.total ?? (d.data?.length ?? d.items?.length ?? 0);
    };
    try {
      const [hubR, openR, pickupR, swapR, leaveR, tsR, tripR] = await Promise.allSettled([
        api.get('/staff/my-shifts-hub'),
        hasPermission('roster.open_shift_pickup') ? api.get('/roster/open-shifts', { params: { page: 1, page_size: 1 } }) : Promise.resolve(null),
        hasPermission('roster.open_shift_pickup') ? api.get('/roster/my-pickup-requests') : Promise.resolve(null),
        hasPermission('roster.request_swap') ? api.get('/roster/swap-requests', { params: { my_requests: true, status: 'REQUESTED' } }) : Promise.resolve(null),
        hasPermission('leave.request') ? api.get('/leave/my', { params: { status: 'PENDING' } }) : Promise.resolve(null),
        hasPermission('timesheets.view_own') ? api.get('/timesheets/v2/my', { params: { status: 'SUBMITTED' } }) : Promise.resolve(null),
        hasPermission('transport.log') ? api.get('/transport/logs', { params: { status: 'SUBMITTED' } }).catch(() => null) : Promise.resolve(null),
      ]);
      setHub(hubR.status === 'fulfilled' ? hubR.value?.data?.data : null);
      setOpenShiftCount(cnt(openR));
      const breakdown = [
        { key: 'pickup', label: 'Open-shift requests', count: cnt(pickupR), to: '/staff/open-shifts' },
        { key: 'swaps', label: 'Shift swaps', count: cnt(swapR), to: '/my/swap-requests' },
        { key: 'leave', label: 'Leave requests', count: cnt(leaveR), to: '/portal/leave' },
        { key: 'timesheets', label: 'Submitted timesheets', count: cnt(tsR), to: '/timesheets/my-v2' },
        { key: 'trips', label: 'Trip submissions', count: cnt(tripR), to: '/my/trip-logs' },
      ].filter((b) => b.count !== null);
      setPending(breakdown);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [hasPermission]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [load]);

  const pendingTotal = (pending || []).reduce((s, b) => s + (b.count || 0), 0);
  const next = hub?.current_shift || hub?.next_shift;

  return (
    <div className="space-y-6" data-testid="my-shifts-hub">
      <div className="flex items-center justify-end">
        <Button variant="outline" size="sm" onClick={() => { setRefreshing(true); load(); }} disabled={refreshing} data-testid="my-work-refresh">
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="my-shifts-summary">
        <Card data-testid="hub-next-shift">
          <CardHeader className="pb-1"><CardTitle className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" />Next Shift</CardTitle></CardHeader>
          <CardContent aria-live="polite">
            {loading ? <div className="h-10 animate-pulse bg-muted rounded" /> : next ? (
              <div className="text-sm space-y-0.5">
                <div className="font-semibold">{next.date} · {next.start_time}–{next.end_time}</div>
                <div className="text-muted-foreground truncate">{next.participant_name || '—'}{next.location ? ` · ${next.location}` : ''}</div>
                {next.notes && <div className="text-xs italic text-muted-foreground line-clamp-2" title={next.notes} data-testid="hub-next-shift-notes">{next.notes}</div>}
                <div className="flex items-center gap-2 pt-1">
                  {next.check_in_time && !next.check_out_time && <Badge className="bg-emerald-500">Clocked in</Badge>}
                  <Button asChild variant="ghost" size="sm" className="h-7 px-2" data-testid="hub-view-shift">
                    <Link to={`/portal/shift/${next.id}`}>View shift <ChevronRight className="h-3 w-3 ml-1" /></Link>
                  </Button>
                </div>
              </div>
            ) : <div className="text-sm text-muted-foreground py-2" data-testid="hub-next-shift-empty">No upcoming shift assigned.</div>}
          </CardContent>
        </Card>
        <Card data-testid="hub-hours-week">
          <CardHeader className="pb-1"><CardTitle className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1.5"><Timer className="h-3.5 w-3.5" />Scheduled Hours This Week</CardTitle></CardHeader>
          <CardContent>
            {loading ? <div className="h-10 animate-pulse bg-muted rounded" /> : (
              <>
                <div className="text-2xl font-bold tabular-nums">{hub ? hub.scheduled_hours_this_week : '—'}</div>
                <div className="text-[11px] text-muted-foreground">Rostered {hub?.week_start} → {hub?.week_end}. Worked/approved hours are on Timesheets.</div>
              </>
            )}
          </CardContent>
        </Card>
        <Link to="/staff/open-shifts" className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg">
          <Card className="h-full hover:border-primary/40" data-testid="hub-open-shifts">
            <CardHeader className="pb-1"><CardTitle className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />Open Shifts</CardTitle></CardHeader>
            <CardContent>
              <div className="text-2xl font-bold tabular-nums">{loading ? '…' : (openShiftCount ?? '—')}</div>
              <div className="text-[11px] text-muted-foreground">Eligible for you (server-checked)</div>
            </CardContent>
          </Card>
        </Link>
        <Card data-testid="hub-pending-requests">
          <CardHeader className="pb-1"><CardTitle className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" />Pending Requests</CardTitle></CardHeader>
          <CardContent>
            <div className="text-2xl font-bold tabular-nums" data-testid="hub-pending-total">{loading ? '…' : pendingTotal}</div>
            <div className="space-y-0.5 mt-1">
              {(pending || []).filter((b) => b.count > 0).map((b) => (
                <Link key={b.key} to={b.to} className="flex justify-between text-[12px] text-muted-foreground hover:text-primary" data-testid={`hub-pending-${b.key}`}>
                  <span>{b.label}</span><span className="tabular-nums font-medium">{b.count}</span>
                </Link>
              ))}
              {!loading && pendingTotal === 0 && <div className="text-[12px] text-muted-foreground">Nothing awaiting action.</div>}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Grouped destinations */}
      {GROUPS.map((g) => {
        const visible = g.items.filter((c) => c.permissions.some((p) => hasPermission(p)));
        if (visible.length === 0) return null;
        return (
          <section key={g.title} aria-label={g.title}>
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">{g.title}</h2>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {visible.map((c) => {
                const Icon = c.icon;
                return (
                  <Link key={c.key} to={c.path} className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg">
                    <Card className="hover:border-primary/40 hover:shadow-sm transition-colors" data-testid={`my-work-card-${c.key}`}>
                      <CardContent className="py-4 flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0"><Icon className="h-4.5 w-4.5" /></div>
                        <div className="min-w-0">
                          <div className="font-medium text-sm">{c.title}</div>
                          {c.note && <div className="text-[11px] text-muted-foreground">{c.note}</div>}
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground ml-auto" />
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}
